import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Smartphone, CheckCircle2 } from "lucide-react";

export default function Security() {
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null); // {secret, otpauth_url, qr_png_base64}
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [loading, setLoading] = useState(false);

  const loadStatus = async () => {
    try { const { data } = await api.get("/auth/2fa/status"); setStatus(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setLoading(true);
    try { const { data } = await api.post("/auth/2fa/setup"); setSetupData(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const confirmEnable = async () => {
    if (code.length !== 6) return toast.error("Ingrese el código de 6 dígitos");
    setLoading(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("2FA activado correctamente");
      setSetupData(null); setCode(""); loadStatus();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const disable = async () => {
    if (disableCode.length !== 6) return toast.error("Ingrese el código de 6 dígitos para confirmar");
    setLoading(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      toast.success("2FA desactivado");
      setDisableCode(""); loadStatus();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="security-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Seguridad</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Mi cuenta — Autenticación en 2 pasos</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Protege el acceso al sistema exigiendo un código temporal generado en tu teléfono además de tu contraseña.</p>
      </div>

      <Card className="p-6 border border-[#d1dbd6]">
        {status === null && <div className="text-sm text-[#8ba397]">Cargando...</div>}
        {status && status.totp_enabled && !setupData && (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#e9f2ee] grid place-items-center">
                <ShieldCheck className="text-[#205c45]" size={20} />
              </div>
              <div>
                <div className="font-semibold text-[#15241d]">2FA activo</div>
                <div className="text-xs text-[#5c7368]">Tu cuenta requiere un código de tu app autenticadora al iniciar sesión.</div>
              </div>
            </div>
            <div className="border-t border-[#d1dbd6] pt-4 mt-4 space-y-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Desactivar 2FA</div>
              <Label>Ingrese un código válido para confirmar</Label>
              <Input
                inputMode="numeric" maxLength={6}
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="h-11 max-w-[220px] text-center tracking-[0.3em] font-mono text-lg"
                data-testid="disable-2fa-code"
              />
              <Button variant="outline" onClick={disable} disabled={loading || disableCode.length < 6} className="border-[#d99584] text-[#7a2514] hover:bg-[#f5d6cf]" data-testid="disable-2fa-btn">
                <ShieldAlert size={16} className="mr-2" />Desactivar 2FA
              </Button>
            </div>
          </>
        )}

        {status && !status.totp_enabled && !setupData && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#fef7ec] grid place-items-center">
                <ShieldAlert className="text-[#8a5500]" size={20} />
              </div>
              <div>
                <div className="font-semibold text-[#15241d]">2FA no está activado</div>
                <div className="text-xs text-[#5c7368]">Recomendado para cuentas admin y gerencia.</div>
              </div>
            </div>
            <div className="text-sm text-[#4a5c53] mb-4 leading-relaxed">
              Usa una app como <strong>Google Authenticator</strong>, <strong>Microsoft Authenticator</strong> o <strong>Authy</strong>
              para generar códigos temporales de 6 dígitos que cambian cada 30 segundos.
            </div>
            <Button onClick={startSetup} disabled={loading} className="bg-[#205c45]" data-testid="setup-2fa-btn">
              <Smartphone size={16} className="mr-2" />Activar 2FA
            </Button>
          </>
        )}

        {setupData && (
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-3">Paso 1 — Escanea el código QR</div>
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="rounded-md border border-[#d1dbd6] bg-white p-3">
                <img src={`data:image/png;base64,${setupData.qr_png_base64}`} alt="QR 2FA" className="w-48 h-48" data-testid="2fa-qr" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="text-sm text-[#4a5c53]">
                  Escanea el QR con tu app autenticadora.
                </div>
                <div className="text-xs text-[#5c7368]">¿No puedes escanear? Ingresa manualmente esta clave:</div>
                <div className="font-mono text-xs bg-[#f7f9f8] border border-[#d1dbd6] rounded px-3 py-2 break-all" data-testid="2fa-secret">
                  {setupData.secret}
                </div>
              </div>
            </div>
            <div className="mt-6 border-t border-[#d1dbd6] pt-4 space-y-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Paso 2 — Verifica con el código actual</div>
              <Label>Ingresa el código de 6 dígitos que muestra tu app</Label>
              <Input
                inputMode="numeric" maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="h-12 max-w-[240px] text-center tracking-[0.4em] font-mono text-xl"
                data-testid="2fa-setup-code"
                autoFocus
              />
              <div className="flex gap-2">
                <Button onClick={confirmEnable} disabled={loading || code.length < 6} className="bg-[#205c45]" data-testid="confirm-2fa-btn">
                  <CheckCircle2 size={16} className="mr-2" />Activar 2FA
                </Button>
                <Button variant="outline" onClick={() => { setSetupData(null); setCode(""); }}>Cancelar</Button>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
